import { img } from "../images";
import type { Service, ServiceCategory } from "./types";

export const hairCategory: ServiceCategory = {
  slug: "hair-care",
  name: "Hair & Scalp",
  short: "Expert care for hair and scalp health.",
  tagline: "Specialist care for hair fall, dandruff and scalp health.",
  image: img.hair,
  doctorSlug: "dr-m-vanitha",
  intro:
    "Hair and scalp concerns are common and often cause significant worry. At AV Clinic, hair fall, dandruff and scalp disorders are evaluated thoroughly to understand the cause before recommending a management plan.",
  description:
    "Healthy hair starts with a healthy scalp. Hair and scalp problems — from everyday dandruff and hair fall to more persistent thinning or scalp irritation — can be distressing, and many people are unsure what is normal and what needs attention. At AV Clinic, hair and scalp care begins with a structured evaluation: the pattern of hair loss, the condition of the scalp and the factors that may be contributing are all reviewed together. This helps distinguish normal shedding from a concern that warrants treatment, and guides a practical, personalised plan. Because hair responds to treatment gradually, the emphasis is on understanding your individual situation, setting realistic expectations and building a routine you can maintain for long-term hair health.",
  conditions: [
    "hair-loss",
    "dandruff",
    "scalp-disorders",
    "hair-scalp-evaluation",
    "hair-health-consultation",
  ],
  concerns: [
    "Hair fall",
    "Hair thinning",
    "Dandruff",
    "Scalp disorders",
    "Itchy or flaky scalp",
  ],
  management: [
    "Detailed hair and scalp evaluation",
    "Identifying the cause of hair fall and thinning",
    "Personalised hair health and care guidance",
    "Management of dandruff and scalp conditions",
    "Ongoing monitoring of hair health",
  ],
  faqs: [
    {
      q: "Is hair fall normal?",
      a: "Some daily hair shedding is normal, but persistent or excessive hair fall may need evaluation. A specialist can identify treatable causes and guide you on the right approach.",
    },
    {
      q: "Does washing hair cause hair loss?",
      a: "No. Washing removes hairs that were already shed, so it can look like more hair loss than is actually happening.",
    },
    {
      q: "How quickly do hair treatments work?",
      a: "Hair grows slowly, so most treatments take several months to show visible improvement. Consistency and follow-up are important.",
    },
  ],
  related: ["hair-loss", "hair-scalp-evaluation", "hair-health-consultation", "general-dermatology"],
  diagnosisSteps: [
    { title: "Hair & Scalp Assessment", desc: "The pattern of hair loss and scalp condition are examined." },
    { title: "History Review", desc: "Genetics, nutrition, stress, hormones and hair-care habits are discussed." },
    { title: "Simple Checks", desc: "Gentle pulls and scalp inspection help assess shedding and health." },
    { title: "Personalised Plan", desc: "A realistic hair and scalp care plan is agreed." },
  ],
  specialistApproach: [
    { icon: "ri-headphone-line", title: "Listen", desc: "Understand your hair concerns and how they affect you." },
    { icon: "ri-search-eye-line", title: "Assess", desc: "Examine the hair pattern and scalp in detail." },
    { icon: "ri-stethoscope-line", title: "Diagnose", desc: "Identify the likely cause of shedding or thinning." },
    { icon: "ri-calendar-check-line", title: "Plan", desc: "Build a sustainable hair-care and treatment plan." },
  ],
  patientEducation: [
    { icon: "ri-list-check-2", title: "Before Your Visit", desc: "Note when shedding increased, any stress or illness, and products you use." },
    { icon: "ri-image-2-line", title: "Bring Along", desc: "Photos from a few months ago can help show the pattern of loss." },
    { icon: "ri-question-line", title: "Questions to Ask", desc: "Ask about realistic timelines, causes, diet and when to follow up." },
    { icon: "ri-refresh-line", title: "Stay Consistent", desc: "Hair responds to treatment slowly, so a consistent routine matters most." },
  ],
  myths: [
    { myth: "Frequent cutting makes hair grow faster.", fact: "Trimming removes split ends but does not change how fast hair grows from the scalp." },
    { myth: "Wearing caps causes hair loss.", fact: "Hats do not cause hair loss; tight hairstyles that pull on the roots can." },
  ],
  seo: {
    title: "Hair Specialist in Krishnagiri | Hair Fall & Scalp Care | AV Clinic",
    description:
      "Specialist hair and scalp care in Krishnagiri — hair fall, thinning, dandruff and scalp disorders evaluated and managed by Dr. M. Vanitha at AV Clinic. Book a consultation.",
  },
};

export const hairServices: Service[] = [
  {
    slug: "hair-loss",
    name: "Hair Loss",
    category: "Hair & Trichology",
    categorySlug: "hair-care",
    blurb: "Understanding the cause of your hair fall and planning the right care.",
    tagline: "Understanding Hair Loss. Finding the Right Care.",
    intro:
      "Hair loss can stem from genetics, nutrition, stress, hormonal changes or medical conditions. AV Clinic evaluates hair loss to identify its cause and guide appropriate, realistic management.",
    image: img.hair,
    symptoms: [
      { name: "Increased shedding", desc: "More hair than usual in the shower, brush or on your pillow." },
      { name: "Generalised thinning", desc: "Hair looks overall less dense, often at the crown or parting." },
      { name: "Receding hairline", desc: "A hairline that moves back over time." },
      { name: "Patchy hair loss", desc: "Round, smooth bald patches on the scalp." },
      { name: "Scalp changes", desc: "Itching, flaking or soreness alongside hair loss." },
    ],
    causes: [
      "Genetics (pattern hair loss)",
      "Hormonal changes",
      "Stress and illness",
      "Nutritional factors (iron, protein, vitamins)",
      "Medical conditions or medications",
      "Hairstyling practices",
    ],
    types: [
      { name: "Pattern hair loss", desc: "Genetic thinning, typically at the crown or hairline." },
      { name: "Diffuse hair loss", desc: "Even shedding across the scalp, often stress- or nutrition-related." },
      { name: "Patchy hair loss", desc: "Localised bald patches, such as in alopecia areata." },
    ],
    riskFactors: [
      "Family history of hair loss",
      "Recent stress, illness or surgery",
      "Poor nutrition or rapid weight loss",
      "Hormonal changes",
      "Tight hairstyles or chemical treatments",
    ],
    whenToSee: [
      "Shedding is excessive or persistent",
      "Hair is visibly thinning",
      "You notice bald patches",
      "Hair loss is affecting your confidence",
    ],
    diagnosisSteps: [
      { title: "Hair & Scalp Assessment", desc: "The pattern and density of hair are examined." },
      { title: "History Review", desc: "Genetics, nutrition, stress and hormones are discussed." },
      { title: "Simple Checks", desc: "Gentle pull tests and scalp inspection guide the assessment." },
      { title: "Personalised Plan", desc: "Realistic options for your type of hair loss are agreed." },
    ],
    treatment: {
      medical: [
        "Treatments matched to the cause and pattern",
        "Guidance on appropriate medical options",
      ],
      procedures: [],
      lifestyle: [
        "Balanced nutrition and adequate protein",
        "Gentle hair handling and styling",
        "Managing stress and sleep",
      ],
      followUp:
        "Hair grows slowly, so improvement takes months. Regular review tracks progress and adjusts the plan.",
    },
    myths: [
      { myth: "Hair loss is only inherited.", fact: "Genetics play a role, but stress, nutrition, hormones and illness also contribute and are often treatable." },
      { myth: "Nothing can be done for hair loss.", fact: "Many causes of hair loss can be treated or slowed, especially when addressed early." },
    ],
    prevention: [
      "Eat a balanced, protein-rich diet",
      "Handle hair gently",
      "Avoid very tight hairstyles",
      "Manage stress",
    ],
    warningSigns: [
      "Sudden, rapid or patchy hair loss",
      "Hair loss with scalp pain, redness or scaling",
      "Hair loss after starting a new medicine",
    ],
    management: [
      "Detailed history and scalp examination",
      "Identification of underlying causes",
      "Personalised treatment and care plan",
      "Lifestyle and nutrition guidance",
      "Monitoring of progress",
    ],
    faqs: [
      { q: "Is hair loss reversible?", a: "Many causes of hair loss can be treated and reversed if addressed early. A specialist evaluation helps determine what is possible for your situation." },
      { q: "How much hair fall is normal?", a: "It is normal to shed 50 to 100 hairs a day. Excessive or persistent shedding warrants evaluation." },
      { q: "Does diet cause hair loss?", a: "Nutritional deficiencies, especially iron and protein, can contribute. A balanced diet supports healthy hair." },
      { q: "When will I see results?", a: "Hair grows slowly; most treatments need several months before visible improvement." },
      { q: "Can stress cause hair loss?", a: "Yes. Significant stress or illness can trigger temporary shedding, which often recovers with time." },
      { q: "How can I book a consultation?", a: "You can book through the appointment page, call the clinic, or message us on WhatsApp." },
    ],
    related: ["hair-scalp-evaluation", "hair-health-consultation", "scalp-disorders", "general-dermatology"],
    relatedBlogs: ["hair-fall-common-causes", "hair-thinning-signs-to-notice"],
    seo: {
      title: "Hair Loss Treatment in Krishnagiri | Hair Specialist | AV Clinic",
      description:
        "Hair loss treatment in Krishnagiri — understand the cause of hair fall and thinning with specialist evaluation from a hair specialist at AV Clinic.",
    },
  },
  {
    slug: "dandruff",
    name: "Dandruff",
    category: "Hair & Trichology",
    categorySlug: "hair-care",
    blurb: "Controlling flaky, itchy scalp and keeping dandruff at bay.",
    tagline: "A Flake-Free, Comfortable Scalp.",
    intro:
      "Dandruff causes flaking and itching of the scalp. It is very common and manageable with the right scalp care and treatment. AV Clinic helps you bring your scalp back into balance.",
    image: img.hairCare,
    symptoms: [
      { name: "White flakes", desc: "Visible flakes on the scalp and hair." },
      { name: "Itchy scalp", desc: "Persistent itching that can be bothersome." },
      { name: "Flakes on shoulders", desc: "Flakes that fall onto clothing." },
    ],
    causes: [
      "Overgrowth of a naturally occurring scalp yeast",
      "Excess oil on the scalp",
      "Dry skin",
      "Sensitivity to hair products",
    ],
    riskFactors: [
      "Oily skin",
      "Stress",
      "Weather changes",
      "Infrequent washing",
    ],
    whenToSee: [
      "Dandruff is severe or not improving",
      "Itching is intense",
      "Accompanied by hair loss or scalp redness",
    ],
    diagnosisSteps: [
      { title: "Scalp Evaluation", desc: "The scalp and hair are examined." },
      { title: "Cause Review", desc: "Oily, dry or product-related factors are considered." },
      { title: "Care Plan", desc: "The right shampoo and washing routine are advised." },
      { title: "Follow-Up", desc: "Adjustments are made if dandruff recurs." },
    ],
    treatment: {
      medical: [
        "Anti-dandruff shampoos matched to your scalp",
        "Treatment for any associated scalp inflammation",
      ],
      procedures: [],
      lifestyle: [
        "Regular washing with the right shampoo",
        "Gentle scalp care",
        "Avoiding harsh products",
      ],
      followUp:
        "Dandruff can recur, so ongoing use of the right care routine is often needed to keep it controlled.",
    },
    myths: [
      { myth: "Dandruff means a dirty scalp.", fact: "Dandruff is related to oil, yeast and scalp sensitivity — not poor hygiene." },
      { myth: "Washing more will cure dandruff.", fact: "Regular washing with the right shampoo helps, but over-washing can irritate the scalp." },
    ],
    prevention: [
      "Wash hair regularly",
      "Use a suitable shampoo",
      "Manage stress",
      "Avoid harsh styling products",
    ],
    management: [
      "Scalp evaluation",
      "Appropriate anti-dandruff treatment",
      "Scalp-care and washing guidance",
    ],
    faqs: [
      { q: "Why does dandruff keep coming back?", a: "Dandruff can recur because the scalp environment favours flaking. Consistent use of the right shampoo and care helps keep it controlled." },
      { q: "How often should I wash my hair?", a: "This depends on your scalp type, but regular washing with an appropriate shampoo is usually recommended." },
      { q: "Can dandruff cause hair loss?", a: "Dandruff itself rarely causes hair loss, but scratching an irritated scalp can damage hair." },
    ],
    related: ["scalp-disorders", "hair-loss", "hair-scalp-evaluation"],
    relatedBlogs: ["dandruff-itchy-scalp-care"],
    seo: {
      title: "Dandruff Treatment in Krishnagiri | Scalp Care | AV Clinic",
      description:
        "Dandruff treatment in Krishnagiri — control flaky, itchy scalp with the right care and anti-dandruff treatment from a hair specialist at AV Clinic.",
    },
  },
  {
    slug: "scalp-disorders",
    name: "Scalp Disorders",
    category: "Hair & Trichology",
    categorySlug: "hair-care",
    blurb: "Care for an itchy, flaky or inflamed scalp.",
    tagline: "A Healthier Scalp for Healthier Hair.",
    intro:
      "Scalp conditions such as seborrheic dermatitis, psoriasis of the scalp and folliculitis can cause itching, flaking and even hair loss. Specialist care addresses the underlying condition affecting your scalp.",
    image: img.hairCare,
    symptoms: [
      { name: "Itchy or burning scalp", desc: "Persistent itching or burning discomfort." },
      { name: "Flaking and redness", desc: "Visible scaling with areas of redness." },
      { name: "Scalp pimples", desc: "Sore, inflamed bumps (folliculitis)." },
      { name: "Hair loss", desc: "Shedding or thinning over affected areas." },
    ],
    causes: [
      "Seborrheic dermatitis",
      "Scalp psoriasis",
      "Folliculitis or infections",
      "Sensitivity to products",
      "Skin conditions affecting the scalp",
    ],
    riskFactors: [
      "Oily or sensitive skin",
      "Stress",
      "Existing skin conditions",
      "Harsh hair products",
    ],
    whenToSee: [
      "Scalp is painful, oozing or severely itchy",
      "Flaking is not improving",
      "Accompanied by hair loss",
    ],
    diagnosisSteps: [
      { title: "Scalp Examination", desc: "The scalp and hair roots are examined closely." },
      { title: "Cause Assessment", desc: "The underlying scalp condition is identified." },
      { title: "Treatment Plan", desc: "Medicated care matched to the condition is given." },
      { title: "Prevention", desc: "Guidance to prevent recurrence is provided." },
    ],
    treatment: {
      medical: [
        "Medicated shampoos and topical treatments",
        "Care matched to the specific scalp condition",
      ],
      procedures: [],
      lifestyle: [
        "Gentle scalp care",
        "Avoiding harsh products",
        "Managing stress",
      ],
      followUp:
        "Chronic scalp conditions need ongoing management. Review helps adjust treatment and control flare-ups.",
    },
    myths: [
      { myth: "Scalp problems are just dandruff.", fact: "Several conditions affect the scalp. Proper evaluation identifies the correct one." },
    ],
    prevention: [
      "Use gentle, suitable hair products",
      "Wash hair regularly",
      "Avoid scratching",
    ],
    management: [
      "Scalp and hair evaluation",
      "Treatment of the underlying scalp condition",
      "Guidance to prevent recurrence",
    ],
    faqs: [
      { q: "Can scalp problems cause hair loss?", a: "Yes. Conditions that inflame the scalp can affect hair growth. Treating the scalp condition often helps improve hair health." },
      { q: "Is scalp psoriasis the same as dandruff?", a: "No. Scalp psoriasis is a distinct immune-related condition that needs specific management." },
    ],
    related: ["dandruff", "hair-loss", "psoriasis"],
    relatedBlogs: ["dandruff-itchy-scalp-care"],
    seo: {
      title: "Scalp Disorder Treatment in Krishnagiri | Hair Specialist | AV Clinic",
      description:
        "Scalp disorder treatment in Krishnagiri — care for itchy, flaky or inflamed scalp from a hair and skin specialist at AV Clinic. Book a consultation.",
    },
  },
  {
    slug: "hair-scalp-evaluation",
    name: "Hair & Scalp Evaluation",
    category: "Hair & Trichology",
    categorySlug: "hair-care",
    blurb: "A thorough assessment to understand your hair and scalp health.",
    tagline: "A Clear Picture of Your Hair & Scalp.",
    intro:
      "A dedicated hair and scalp evaluation examines the pattern, density and condition of your hair and scalp to guide appropriate care and give you a clear understanding of your hair health.",
    image: img.hairCare,
    symptoms: [
      { name: "Concern about density", desc: "Feeling that hair is getting thinner." },
      { name: "Texture changes", desc: "Hair that feels finer, duller or more brittle." },
      { name: "Scalp symptoms", desc: "Itching, flaking or soreness." },
    ],
    causes: [
      "Multiple possible factors",
      "Genetics, nutrition and stress",
      "Hair-care habits",
    ],
    whenToSee: [
      "You are unsure whether hair fall is normal",
      "You want a baseline understanding of hair health",
      "Changes are progressive",
    ],
    diagnosisSteps: [
      { title: "Structured Assessment", desc: "Hair density, pattern and scalp are evaluated." },
      { title: "History & Habits", desc: "Diet, stress, styling and products are reviewed." },
      { title: "Findings Explained", desc: "Results are explained in plain language." },
      { title: "Recommendations", desc: "Practical, personalised advice is given." },
    ],
    treatment: {
      medical: [],
      procedures: [],
      lifestyle: [
        "Balanced diet and hydration",
        "Gentle hair care",
        "Reducing stress",
      ],
      followUp:
        "Depending on findings, a follow-up may be recommended to monitor changes over time.",
    },
    management: [
      "Structured hair and scalp assessment",
      "Discussion of findings in plain language",
      "Personalised care recommendations",
    ],
    faqs: [
      { q: "What does a hair evaluation involve?", a: "It involves examining your scalp and hair, discussing your history, and may include simple checks to assess hair health." },
      { q: "Do I need any tests?", a: "Not always. Tests are only arranged if the history or examination suggests a specific cause." },
    ],
    related: ["hair-loss", "hair-health-consultation"],
    relatedBlogs: ["hair-thinning-signs-to-notice"],
    seo: {
      title: "Hair & Scalp Evaluation in Krishnagiri | AV Clinic",
      description:
        "Thorough hair and scalp evaluation in Krishnagiri to understand hair health and guide care. Specialist assessment at AV Clinic.",
    },
  },
  {
    slug: "hair-health-consultation",
    name: "Hair Health Consultation",
    category: "Hair & Trichology",
    categorySlug: "hair-care",
    blurb: "Personalised advice to protect and improve your hair.",
    tagline: "Protect Your Hair, Proactively.",
    intro:
      "Prevention is better than cure. A hair health consultation helps you build a sustainable routine for stronger, healthier hair, whether you have a current concern or simply want to protect your hair.",
    image: img.hairCare,
    symptoms: [
      { name: "Preventive care goals", desc: "Wanting to maintain healthy hair." },
      { name: "Routine concerns", desc: "Unsure if your current hair care is right." },
      { name: "Nutrition questions", desc: "Wondering how diet affects hair." },
    ],
    causes: [
      "Hair-care routine and products",
      "Nutrition and hydration",
      "Styling practices",
    ],
    whenToSee: [
      "You want to build a healthy hair routine",
      "You have questions about diet and hair",
      "You want to prevent future hair loss",
    ],
    diagnosisSteps: [
      { title: "Routine Review", desc: "Your current hair-care habits are discussed." },
      { title: "Lifestyle & Diet", desc: "Nutrition and lifestyle factors are reviewed." },
      { title: "Practical Advice", desc: "Realistic, sustainable changes are suggested." },
      { title: "Follow-Up Plan", desc: "A plan to monitor hair health is agreed." },
    ],
    treatment: {
      medical: [],
      procedures: [],
      lifestyle: [
        "Balanced, protein-rich nutrition",
        "Gentle washing and styling",
        "Protection from heat and chemical stress",
      ],
      followUp:
        "A review helps track how your hair responds and fine-tune the routine.",
    },
    management: [
      "Review of hair care routine",
      "Nutritional and lifestyle guidance",
      "Advice on gentle hair management",
    ],
    faqs: [
      { q: "Does diet affect hair health?", a: "Yes. A balanced diet with adequate protein, iron and vitamins supports healthy hair growth." },
      { q: "How can I prevent hair damage?", a: "Gentle handling, avoiding excessive heat and chemicals, and a good routine help protect hair." },
    ],
    related: ["hair-loss", "hair-scalp-evaluation"],
    relatedBlogs: ["hair-thinning-signs-to-notice"],
    seo: {
      title: "Hair Health Consultation in Krishnagiri | AV Clinic",
      description:
        "Personalised hair health consultation in Krishnagiri — build a sustainable routine for stronger, healthier hair with a specialist at AV Clinic.",
    },
  },
];